import { DEFAULT_COUNTRY } from "hooks/useSession";
import moment from "moment";
import { RootState, store } from "store";
import { DATE_FORMATS, TIMEZONES } from "./constants";

const getCountryCode = () => {
  const state = store.getState() as RootState;
  return state.session.countryCode || DEFAULT_COUNTRY;
};

export function getDifDate(date: string) {
  const regDate = moment();
  const countryCode = getCountryCode();
  const offset = TIMEZONES[countryCode];
  const actualDate = moment(date, DATE_FORMATS.fullISODate).add(
    offset,
    "hours"
  );
  return regDate.diff(actualDate, "days");
}

export const ISO8601toDDMMYYYHHMM = (value: string, timezone?: number) => {
  const date = new Date(value);
  const countryCode = getCountryCode();
  const offset = timezone || TIMEZONES[countryCode];
  date.setHours(date.getHours() + offset);
  const DDMMYYYY = moment(date.toISOString().slice(0, 10)).format(
    DATE_FORMATS.shortDate
  );
  const dateHHMM = date.toISOString().slice(11, 16);
  return DDMMYYYY + " " + dateHHMM;
};

export const compensateTime = (value: string, timezone?: number) => {
  const countryCode = getCountryCode();
  const offset = timezone || TIMEZONES[countryCode];
  const [hours, minutes] = value.split(":");
  const hh =
    Number(hours) + offset < 0
      ? `${24 + (Number(hours) + offset)}`
      : Number(hours) + offset < 10
      ? `0${Number(hours) + offset}`
      : `${Number(hours) + offset}`;
  return `${hh}:${minutes}`;
};

export const getTimezoneOffset = (timezone: number) => {
  const date = new Date();
  const localTimezone = (date.getTimezoneOffset() * -1) / 60;
  const deltaTimezone = localTimezone + timezone;
  return deltaTimezone;
};

export const getLocalTimezoneOffset = () => {
  const date = new Date();
  const countryCode = getCountryCode();
  const localTimezone = (date.getTimezoneOffset() * -1) / 60;
  const deltaTimezone = TIMEZONES[countryCode] - localTimezone;
  return deltaTimezone;
};

export const formatDDMMYYYY = (value: string, timezone?: number) => {
  return ISO8601toDDMMYYYHHMM(value, timezone).substring(0, 10);
};

export const formatHHMM = (value: string, timezone?: number) => {
  return ISO8601toDDMMYYYHHMM(value, timezone).substring(11, 16);
};

export const isDateBeforeToday = (value: string | undefined) => {
  const offset = getLocalTimezoneOffset() || 0;
  const now = moment().add(offset, "hours");
  const date = moment(value, DATE_FORMATS.shortDate);
  return date.isBefore(now, "days");
};

export const isTodayTimeBeforeNow = (dateValue: string, hourValue: string) => {
  const offset = getLocalTimezoneOffset() || 0;
  const now = moment().add(offset, "hours");
  const date = moment(dateValue, DATE_FORMATS.shortDate);
  if (!date.isSame(now, "days")) return false;
  const [hours, minutes] = hourValue.split(":");
  const time = date.add(hours, "hours").add(minutes, "minutes");
  return time.isBefore(now, "minutes");
};

export const isToday = (dateValue: string) => {
  const offset = getLocalTimezoneOffset() || 0;
  const now = moment().add(offset, "hours");
  const date = moment(dateValue, DATE_FORMATS.shortDate);
  return date.isSame(now, "days");
};

export const isISOTimeAfterNow = (value: string | undefined) => {
  const offset = getLocalTimezoneOffset() || 0;
  const now = moment().add(offset, "hours");
  const date = moment(value).add(offset, "hours");
  return date.isAfter(now, "minutes");
};
